import { useState, useEffect } from "react"
import { useAuth } from "@/hooks/useAuth"
import { supabase } from "@/integrations/supabase/client"
import { useToast } from "@/hooks/use-toast"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger, 
  DialogFooter 
} from "@/components/ui/dialog"
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle, 
  AlertDialogTrigger 
} from "@/components/ui/alert-dialog"
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select"
import { Coffee, Plus, Edit, Trash2, Star, Trophy, Heart, Upload, Image } from "lucide-react"
import { DashboardLayout } from "@/components/layout/DashboardLayout"

interface Reward {
  id: string
  tenant_id: string
  name: string
  description: string
  stamps_required: number
  reward_type: 'discount' | 'free_item' | 'points' | 'special'
  reward_value: string
  is_active: boolean
  created_at: string
  updated_at: string
  usage_count: number
  image_url?: string
}

interface RewardTemplate {
  id: string
  name: string
  description: string
  reward_type: string
  reward_value: string
  image_url?: string
  is_active: boolean
}

export default function VisitRewards() {
  const { user, loading: authLoading } = useAuth()
  const { toast } = useToast()
  const [rewards, setRewards] = useState<Reward[]>([])
  const [templates, setTemplates] = useState<RewardTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingReward, setEditingReward] = useState<Reward | null>(null)
  const [selectedImage, setSelectedImage] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [formData, setFormData] = useState<{
    name: string
    description: string
    reward_type: 'discount' | 'free_item' | 'points' | 'special'
    reward_value: string
    is_active: boolean
  }>({
    name: "",
    description: "",
    reward_type: "discount",
    reward_value: "",
    is_active: true
  })

  useEffect(() => {
    if (!authLoading) {
      fetchData()
    }
  }, [authLoading])

  const fetchData = async () => {
    if (!user?.id) {
      setLoading(false)
      return
    }

    try {
      // Get user's tenant ID first
      const { data: tenantData, error: tenantError } = await supabase
        .from('tenants')
        .select('id')
        .eq('admin_user_id', user.id)
        .maybeSingle()

      if (tenantError) throw tenantError
      
      if (!tenantData) {
        toast({
          title: "Kein Unternehmen",
          description: "Sie haben noch kein Unternehmen zugewiesen. Kontaktieren Sie einen Superadmin.",
          variant: "destructive",
        })
        setLoading(false)
        return
      }

      const tenantId = tenantData.id

      // Fetch visit rewards (normal rewards table)
      const { data: rewardsData, error: rewardsError } = await supabase
        .from('rewards')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false })

      if (rewardsError) throw rewardsError
      setRewards((rewardsData || []) as Reward[])

      // Fetch reward templates (for admins)
      const { data: templatesData, error: templatesError } = await supabase
        .from('reward_templates')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false })

      if (templatesError) {
        console.log('Templates not available or user is not admin')
      } else {
        setTemplates((templatesData || []) as RewardTemplate[])
      }

    } catch (error) {
      console.error('Error fetching data:', error)
      toast({
        title: "Fehler",
        description: "Daten konnten nicht geladen werden.",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleImageUpload = async (file: File): Promise<string | null> => {
    if (!user?.id) return null

    try {
      setUploading(true)
      
      const fileExt = file.name.split('.').pop()
      const fileName = `${user.id}/${Date.now()}.${fileExt}`
      
      const { data, error } = await supabase.storage
        .from('reward-images')
        .upload(fileName, file)

      if (error) throw error

      const { data: urlData } = supabase.storage
        .from('reward-images')
        .getPublicUrl(data.path)

      return urlData.publicUrl
    } catch (error) {
      console.error('Error uploading image:', error)
      toast({
        title: "Fehler",
        description: "Bild konnte nicht hochgeladen werden.",
        variant: "destructive",
      })
      return null
    } finally {
      setUploading(false)
    }
  }

  const handleSaveReward = async () => {
    if (!user?.id) return

    try {
      // Get user's tenant ID
      const { data: tenantData, error: tenantError } = await supabase
        .from('tenants')
        .select('id')
        .eq('admin_user_id', user.id)
        .maybeSingle()

      if (tenantError) throw tenantError
      if (!tenantData) {
        toast({
          title: "Fehler",
          description: "Kein Unternehmen gefunden.",
          variant: "destructive",
        })
        return
      }

      const tenantId = tenantData.id
      let imageUrl = editingReward?.image_url || null

      // Upload image if selected
      if (selectedImage) {
        imageUrl = await handleImageUpload(selectedImage)
      }
      // If we have a template image URL and no new image, use the template image
      else if (!editingReward && formData.name && !imageUrl) {
        // Check if this is being created from a template by finding matching template
        const matchingTemplate = templates.find(t => t.name === formData.name && t.description === formData.description)
        if (matchingTemplate?.image_url) {
          imageUrl = matchingTemplate.image_url
        }
      }

      const rewardData = {
        name: formData.name,
        description: formData.description,
        reward_type: formData.reward_type,
        reward_value: formData.reward_value,
        is_active: formData.is_active,
        image_url: imageUrl,
      }

      if (editingReward) {
        // Update existing reward
        const { error } = await supabase
          .from('rewards')
          .update(rewardData)
          .eq('id', editingReward.id)
          .eq('tenant_id', tenantId)

        if (error) throw error
        
        toast({
          title: "Erfolg",
          description: "Besucherprämie wurde aktualisiert.",
        })
      } else {
        // Create new reward
        const { error } = await supabase
          .from('rewards')
          .insert({
            ...rewardData,
            tenant_id: tenantId,
          })

        if (error) throw error
        
        toast({
          title: "Erfolg",
          description: "Neue Besucherprämie wurde erstellt.",
        })
      }

      await fetchData()
      resetForm()
      setDialogOpen(false)
    } catch (error) {
      console.error('Error saving reward:', error)
      toast({
        title: "Fehler",
        description: "Besucherprämie konnte nicht gespeichert werden.",
        variant: "destructive",
      })
    }
  }

  const handleDeleteReward = async (rewardId: string) => {
    try {
      const { error } = await supabase
        .from('rewards')
        .delete()
        .eq('id', rewardId)

      if (error) throw error

      await fetchData()
      toast({
        title: "Erfolg",
        description: "Besucherprämie wurde gelöscht.",
      })
    } catch (error) {
      console.error('Error deleting reward:', error)
      toast({
        title: "Fehler",
        description: "Besucherprämie konnte nicht gelöscht werden.",
        variant: "destructive",
      })
    }
  }

  const handleToggleActive = async (rewardId: string) => {
    try {
      const reward = rewards.find(r => r.id === rewardId)
      if (!reward) return

      const { error } = await supabase
        .from('rewards')
        .update({ is_active: !reward.is_active })
        .eq('id', rewardId)

      if (error) throw error

      await fetchData()
      toast({
        title: "Erfolg",
        description: "Prämienstatus wurde geändert.",
      })
    } catch (error) {
      console.error('Error toggling reward status:', error)
      toast({
        title: "Fehler",
        description: "Status konnte nicht geändert werden.",
        variant: "destructive",
      })
    }
  }

  const createFromTemplate = (template: RewardTemplate) => {
    setFormData({
      name: template.name,
      description: template.description,
      reward_type: template.reward_type as any,
      reward_value: template.reward_value,
      is_active: true
    })
    // Set template image if available
    if (template.image_url) {
      setSelectedImage(null) // Clear any file, we'll use the URL directly
    }
    setDialogOpen(true)
  }

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      reward_type: "discount",
      reward_value: "",
      is_active: true
    })
    setEditingReward(null)
    setSelectedImage(null)
  }

  const openEditDialog = (reward: Reward) => {
    setEditingReward(reward)
    setFormData({
      name: reward.name,
      description: reward.description,
      reward_type: reward.reward_type,
      reward_value: reward.reward_value,
      is_active: reward.is_active
    })
    setDialogOpen(true)
  }

  const getRewardIcon = (type: string) => {
    switch (type) {
      case 'discount': return <Star className="h-4 w-4" />
      case 'free_item': return <Coffee className="h-4 w-4" />
      case 'points': return <Trophy className="h-4 w-4" />
      default: return <Heart className="h-4 w-4" />
    }
  }

  const getRewardTypeLabel = (type: string) => {
    switch (type) {
      case 'discount': return 'Rabatt'
      case 'free_item': return 'Gratis Artikel'
      case 'points': return 'Punkte'
      case 'special': return 'Spezial'
      default: return type
    }
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="container mx-auto p-6 space-y-6">
          <h1 className="text-3xl font-bold">Besucherprämien verwalten</h1>
          <div className="text-center py-8">
            <div className="text-muted-foreground">Lade Prämien...</div>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div className="container mx-auto p-6 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Besucherprämien verwalten</h1>
            <p className="text-muted-foreground">
              Verwalten Sie Prämien für Ihre treuesten Besucher
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm}>
                <Plus className="h-4 w-4 mr-2" />
                Neue Besucherprämie
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle>
                  {editingReward ? 'Besucherprämie bearbeiten' : 'Neue Besucherprämie erstellen'}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name der Prämie</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="z.B. Gratis Kaffee nach 10 Besuchen"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Beschreibung</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Beschreiben Sie die Prämie..."
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="type">Belohnungstyp</Label>
                  <Select value={formData.reward_type} onValueChange={(value: any) => setFormData(prev => ({ ...prev, reward_type: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="discount">Rabatt</SelectItem>
                      <SelectItem value="free_item">Gratis Artikel</SelectItem>
                      <SelectItem value="points">Punkte</SelectItem>
                      <SelectItem value="special">Spezial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="value">Belohnungswert</Label>
                  <Input
                    id="value"
                    value={formData.reward_value}
                    onChange={(e) => setFormData(prev => ({ ...prev, reward_value: e.target.value }))}
                    placeholder={formData.reward_type === 'discount' ? 'z.B. 10%' : 'z.B. Gratis Kaffee'}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="image">Bild (optional)</Label>
                  <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center">
                    {editingReward?.image_url && !selectedImage ? (
                      <div className="space-y-2">
                        <img 
                          src={editingReward.image_url} 
                          alt="Reward" 
                          className="w-24 h-24 object-cover rounded-lg mx-auto"
                        />
                        <Button
                          variant="outline"
                          onClick={() => {
                            const input = document.createElement('input')
                            input.type = 'file'
                            input.accept = 'image/*'
                            input.onchange = (e) => {
                              const file = (e.target as HTMLInputElement).files?.[0]
                              if (file) setSelectedImage(file)
                            }
                            input.click()
                          }}
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Bild ändern
                        </Button>
                      </div>
                    ) : selectedImage ? (
                      <div className="space-y-2">
                        <img 
                          src={URL.createObjectURL(selectedImage)} 
                          alt="Preview" 
                          className="w-24 h-24 object-cover rounded-lg mx-auto"
                        />
                        <Button
                          variant="outline"
                          onClick={() => setSelectedImage(null)}
                        >
                          Entfernen
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Image className="h-12 w-12 mx-auto text-muted-foreground" />
                        <div className="text-sm text-muted-foreground">
                          <Button
                            variant="outline"
                            onClick={() => {
                              const input = document.createElement('input')
                              input.type = 'file'
                              input.accept = 'image/*'
                              input.onchange = (e) => {
                                const file = (e.target as HTMLInputElement).files?.[0]
                                if (file) setSelectedImage(file)
                              }
                              input.click()
                            }}
                          >
                            <Upload className="h-4 w-4 mr-2" />
                            Bild hochladen
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                  />
                  <Label htmlFor="active">Prämie ist aktiv</Label>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Abbrechen
                </Button>
                <Button onClick={handleSaveReward} disabled={uploading}>
                  {uploading ? 'Speichere...' : editingReward ? 'Aktualisieren' : 'Erstellen'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="bg-primary/10 p-3 rounded-lg">
                  <Coffee className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Gesamt Prämien</p>
                  <p className="text-2xl font-bold">{rewards.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="bg-green-500/10 p-3 rounded-lg">
                  <Star className="h-6 w-6 text-green-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Aktive Prämien</p>
                  <p className="text-2xl font-bold">
                    {rewards.filter(r => r.is_active).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="bg-blue-500/10 p-3 rounded-lg">
                  <Trophy className="h-6 w-6 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Eingelöste Prämien</p>
                  <p className="text-2xl font-bold">
                    {rewards.reduce((sum, r) => sum + r.usage_count, 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Templates Section */}
        {templates.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="h-5 w-5" />
                Vorlagen für Besucherprämien
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.map((template) => (
                  <Card key={template.id} className="border">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {getRewardIcon(template.reward_type)}
                          <h4 className="font-medium">{template.name}</h4>
                        </div>
                        <Badge variant="secondary">
                          {getRewardTypeLabel(template.reward_type)}
                        </Badge>
                      </div>
                      
                      {template.image_url && (
                        <img 
                          src={template.image_url} 
                          alt={template.name}
                          className="w-full h-32 object-cover rounded-lg mb-3"
                        />
                      )}
                      
                      <p className="text-sm text-muted-foreground mb-3">
                        {template.description}
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full"
                        onClick={() => createFromTemplate(template)}
                      >
                        Vorlage verwenden
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Rewards List */}
        <Card>
          <CardHeader>
            <CardTitle>Alle Besucherprämien</CardTitle>
          </CardHeader>
          <CardContent>
            {rewards.length === 0 ? (
              <div className="text-center py-8">
                <Coffee className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Noch keine Besucherprämien</h3>
                <p className="text-muted-foreground mb-4">
                  Erstellen Sie Ihre erste Besucherprämie für treue Kunden
                </p>
                <Button onClick={() => setDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Erste Besucherprämie erstellen
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {rewards.map((reward) => (
                  <div key={reward.id} className="border rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          {getRewardIcon(reward.reward_type)}
                          <h3 className="font-medium">{reward.name}</h3>
                          <Badge variant={reward.is_active ? "default" : "secondary"}>
                            {reward.is_active ? "Aktiv" : "Inaktiv"}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {reward.description}
                        </p>
                        <p className="text-sm">
                          <strong>Wert:</strong> {reward.reward_value} • 
                          <strong> Typ:</strong> {getRewardTypeLabel(reward.reward_type)} • 
                          <strong> Verwendet:</strong> {reward.usage_count}x
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={reward.is_active}
                          onCheckedChange={() => handleToggleActive(reward.id)}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(reward)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="destructive" size="sm">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Besucherprämie löschen</AlertDialogTitle>
                              <AlertDialogDescription>
                                Sind Sie sicher, dass Sie diese Besucherprämie löschen möchten? 
                                Diese Aktion kann nicht rückgängig gemacht werden.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDeleteReward(reward.id)}>
                                Löschen
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}